import { selectRelatedObjectsByObjectName } from '../../../reducers/global/parserReducer';
import { selectRollupsForTable } from '../../../reducers/global/rollupReducers';
import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { findSweepElementsRelatedToObject } from './findSweepElementsRelatedToObject';
import { useGetDeployedVersions } from './useGetDeployedVersions';
import {
  createConfigurationItemFromAutomation,
  createConfigurationItemsFromRelatedObjectsData,
} from '../helpers';
import flatten from 'lodash/flatten';
import { selectAccountUsers } from '../../../reducers/accountUsersReducer';
import { ConfigurationItem } from '../dependencies/DependenciesTypes';
import { RelatedObjectData } from '../../parser/ParserTypes';

export const useGetRelatedObjects = (crmOrgId: string, objectName: string) => {
  const relatedObjects = useSelector(selectRelatedObjectsByObjectName(crmOrgId))?.[
    objectName ?? ''
  ];
  const rollups = useSelector(selectRollupsForTable);
  const deployedVersions = useGetDeployedVersions(crmOrgId);
  const users = useSelector(selectAccountUsers);

  const sweepRelatedObjects = useMemo(() => {
    if (!objectName) {
      return [];
    }
    return flatten([
      ...findSweepElementsRelatedToObject(deployedVersions.automations, objectName),
      ...findSweepElementsRelatedToObject(deployedVersions.alerts, objectName),
      ...findSweepElementsRelatedToObject(deployedVersions.dedup, objectName),
      ...findSweepElementsRelatedToObject(deployedVersions.matching, objectName),
      ...findSweepElementsRelatedToObject(deployedVersions.assignments, objectName),
      ...findSweepElementsRelatedToObject(deployedVersions.scheduledAssignments, objectName),
    ]).map(
      (automation) => createConfigurationItemFromAutomation(automation, users) as ConfigurationItem,
    );
  }, [deployedVersions, objectName, users]);

  // processing related objects is an expensive operation, so we need to run and memoize it separately
  const processedRelatedObjects = useMemo(() => {
    if (!relatedObjects) return [];
    return createConfigurationItemsFromRelatedObjectsData(
      relatedObjects as RelatedObjectData,
      rollups,
    );
  }, [relatedObjects, rollups]);

  const relatedObjectsStructure = useMemo(() => {
    return [...processedRelatedObjects, ...sweepRelatedObjects];
  }, [processedRelatedObjects, sweepRelatedObjects]);

  return relatedObjectsStructure;
};
